import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../../components/Button';
import { ArrowLeft, Upload, X, Loader2, FileText, TestTube , Mail } from 'lucide-react';
import { useAudience } from '../../../context/AudienceContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../../components/ui/dialog.jsx';
import { CustomDialogClose } from '../../../components/ui/dialog-custom';
import { RadioGroup, RadioGroupItem } from '../../../components/ui/radio-group';

interface SegmentPersonaFilters {
  industryL1: string[];
  industryL2: string[];
  functions: string[];
  roles: string[];
}

interface ABTestCreativesProps {
  onSubmit: (simulationId: number) => void;
  selectedSegmentIds: number[];
  personaFilters: Record<number, SegmentPersonaFilters>;
  onBack: () => void;
  onEditStep?: () => void; // Add this prop for edit tracking
  initialFormData?: Record<string, any>; // Add this prop for form state persistence
  onFormDataChange?: (data: Record<string, any>) => void; // Add this prop for notifying parent of changes
  onSavePayload?: (payload: Record<string, any>) => void;
}

interface Segment {
  id: number;
  name: string;
  description: string;
  len: number; // Number of personas
  created_at: string;
  updated_at: string;
}

type ImageFile = {
  id: string;
  file: File;
  preview: string;
};

const API_URL = import.meta.env.VITE_API_URL || '';

const ABTestCreativesForm: React.FC<ABTestCreativesProps> = ({ 
  onSubmit, 
  selectedSegmentIds, 
  personaFilters, 
  onBack,
  onEditStep,
  initialFormData = {},
  onFormDataChange = () => {},
  onSavePayload,
}) => {
  const { audienceData } = useAudience();
  const [segments, setSegments] = useState<Segment[]>([]);
  
  // Initialize form state from initialFormData
  const [simName, setSimName] = useState(initialFormData.simName || '');
  const [goal, setGoal] = useState(initialFormData.goal || '');
  const [reviewType, setReviewType] = useState<'creative-review' | 'ab-test'>(initialFormData.reviewType || 'ab-test');
  const [images, setImages] = useState<ImageFile[]>([]);
  
  // Creative Review specific fields
  const [campaignObjective, setCampaignObjective] = useState(initialFormData.campaignObjective || '');
  const [kpis, setKpis] = useState(initialFormData.kpis || '');
  const [companyDetails, setCompanyDetails] = useState(initialFormData.companyDetails || '');
  const [productDetails, setProductDetails] = useState(initialFormData.productDetails || '');
  const [competitiveLandscape, setCompetitiveLandscape] = useState(initialFormData.competitiveLandscape || '');
  const [mediumAndPlacement, setMediumAndPlacement] = useState(initialFormData.mediumAndPlacement || '');
  const [culturalConsiderations, setCulturalConsiderations] = useState(initialFormData.culturalConsiderations || '');
  const [priorPerformanceData, setPriorPerformanceData] = useState(initialFormData.priorPerformanceData || '');
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showProcessing, setShowProcessing] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const imagesLoadedRef = React.useRef(false);

  // Email notification state
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailNotificationStatus, setEmailNotificationStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [emailNotificationError, setEmailNotificationError] = useState('');
  const [notificationEmail, setNotificationEmail] = useState('');
  
  // Note: We can't persist image files directly, but we'll persist other form data
  useEffect(() => {
    const formData = {
      simName,
      goal,
      reviewType,
      campaignObjective,
      kpis,
      companyDetails,
      productDetails,
      competitiveLandscape,
      mediumAndPlacement,
      culturalConsiderations,
      priorPerformanceData
      // Images can't be serialized, so we won't include them
    };
    
    onFormDataChange(formData);
  }, [simName, goal, reviewType, campaignObjective, kpis, companyDetails, productDetails, competitiveLandscape, mediumAndPlacement, culturalConsiderations, priorPerformanceData]); // Removed onFormDataChange to prevent infinite loop
  
  // Handler to notify parent about edits
  const handleEdit = () => {
    if (onEditStep) {
      onEditStep();
    }
  };
  
  // Initialize images from initialFormData (for Edit & Rerun)
  useEffect(() => {
    if (imagesLoadedRef.current) return;
    
    const initialImages = initialFormData.images || [];
    if (initialImages.length > 0) {
      const loadedImages: ImageFile[] = initialImages.map((src: string, index: number) => ({
        id: `existing-${index}-${Date.now()}`,
        file: new File([], `existing-image-${index}.png`, { type: 'image/png' }),
        preview: src.startsWith('data:') || src.startsWith('http') ? src : `${API_URL}${src}`
      }));
      setImages(loadedImages);
      imagesLoadedRef.current = true;
    }
  }, [initialFormData.images]);

  // Clean up image previews on unmount
  useEffect(() => {
    return () => {
      images.forEach(image => {
        if (image.preview.startsWith('blob:')) {
          URL.revokeObjectURL(image.preview);
        }
      });
    };
  }, [images]);

  useEffect(() => {
    const fetchSegments = async () => {
      if (!audienceData.audienceId) return;
      
      setLoading(true);
      try {
        const response = await fetch(`${API_URL}/audience/${audienceData.audienceId}/segments`, {
          credentials: 'include'
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch segments');
        }
        
        const data = await response.json();
        // Filter segments to only include the selected ones
        const filteredSegments = data.filter((segment: Segment) => 
          selectedSegmentIds.includes(segment.id)
        );
        setSegments(filteredSegments);
        setError(null);
      } catch (err) {
        console.error('Error fetching segments:', err);
        setError('Failed to load segments. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchSegments();
  }, [audienceData.audienceId, selectedSegmentIds]);
  
  // Image handling functions
  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    // Check if adding these files would exceed the 5-image limit
    const remainingSlots = 5 - images.length;
    if (remainingSlots <= 0) {
      alert('Maximum of 5 images allowed. Please remove some images first.');
      return;
    }
    
    const filesToAdd = Array.from(files).slice(0, remainingSlots);
    if (files.length > remainingSlots) {
      alert(`Only ${remainingSlots} more images can be added. ${filesToAdd.length} images will be uploaded.`);
    }
    
    const newImages = filesToAdd.map(file => ({
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`, // Generate unique ID
      file,
      preview: URL.createObjectURL(file)
    }));
    
    setImages([...images, ...newImages]);
    handleEdit();
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  
  const removeImage = (imageId: string) => {
    const imageIndex = images.findIndex(img => img.id === imageId);
    if (imageIndex === -1) return;
    
    const imageToRemove = images[imageIndex];
    
    // Create new array without the image
    const newImages = images.filter(img => img.id !== imageId);
    setImages(newImages);
    
    // Only revoke blob URLs, not http/data URLs
    if (imageToRemove.preview.startsWith('blob:')) {
      setTimeout(() => {
        URL.revokeObjectURL(imageToRemove.preview);
      }, 0);
    }
    
    handleEdit();
  };
  
  const triggerFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };
  
  const saveMode = !!onSavePayload;

  const isFormValid = (saveMode || selectedSegmentIds.length > 0) && 
                      (reviewType === 'ab-test' ? goal.trim() !== '' : true) && 
                      images.length > 0 &&
                      (reviewType === 'ab-test' || 
                       (reviewType === 'creative-review' && 
                        campaignObjective.trim() !== '' && 
                        companyDetails.trim() !== '' && 
                        productDetails.trim() !== ''));

  // Utility function to convert an image to base64 (handles both File and existing URLs)
  const imageToBase64 = async (img: ImageFile): Promise<string> => {
    if (img.preview.startsWith('data:')) {
      return img.preview;
    }
    if (img.preview.startsWith('http') || img.preview.startsWith('/')) {
      try {
        const response = await fetch(img.preview, { credentials: 'include' });
        const blob = await response.blob();
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
      } catch (error) {
        console.error('Error fetching image:', error);
        return img.preview;
      }
    }
    if (img.file.size > 0) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(img.file);
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = error => reject(error);
      });
    }
    return img.preview;
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!isFormValid || isSubmitting) return;
    
    
    setShowProcessing(true);
    setIsSubmitting(true);
    
    try {
      // Convert all images to base64 (handles both existing and new images)
      const imagePromises = images.map(image => imageToBase64(image));
      const base64Images = await Promise.all(imagePromises);
      
      // Submit the simulation with images (processing will happen in background)
      const body = {
        audience_id: audienceData.audienceId,
        task: reviewType === 'creative-review' ? 'creative-review' : 'channel-event-strategy',
        name: simName,
        goal: goal,
        segment_ids: selectedSegmentIds,
        persona_filters: personaFilters, // Include persona filters
        images: base64Images, // Images will be processed in Celery
        ...(reviewType === 'creative-review' && {
          campaign_objective: campaignObjective,
          kpis: kpis,
          company_details: companyDetails,
          product_details: productDetails,
          competitive_landscape: competitiveLandscape,
          medium_and_placement: mediumAndPlacement,
          cultural_considerations: culturalConsiderations,
          prior_performance_data: priorPerformanceData,
        }),
      };

      if (onSavePayload) {
        onSavePayload(body);
        return;
      }

      // Send the request to start a simulation with image uploads and descriptions
      const response = await fetch(`${API_URL}/simulations`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        throw new Error('Failed to start simulation');
      }

      const data = await response.json();
      
      // Call onSubmit with the simulation ID
      onSubmit(data.simulation_id);
    } catch (err) {
      console.error('Error starting simulation:', err);
      setError('Failed to start simulation. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Function to handle email notification registration
  const handleEmailNotification = async (email: string) => {
    setEmailNotificationStatus('sending');
    setEmailNotificationError('');
    
    try {
      const response = await fetch(`${API_URL}/monitor-simulation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          simulationId: null, // We don't have simulation ID yet, backend will handle this
          email: email
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to set up email notification');
      }
      
      setEmailNotificationStatus('success');
      setShowEmailModal(false);
      setNotificationEmail(email);
    } catch (err) {
      console.error('Error setting up email notification:', err);
      setEmailNotificationStatus('error');
      setEmailNotificationError('Failed to set up email notification. Please try again.');
    }
  };

  if (showProcessing) {
    return (
      <div className="relative bg-grey rounded-xl p-6">
        <div className="mb-24"></div>
        <div className="absolute inset-0 z-0 flex flex-col items-center justify-center">
          <div className="animate-spin mb-4">
            <Loader2 className="h-16 w-16 text-blue-500" />
          </div>
          <div className="text-base font-medium mb-2">Simulation Starting...</div>
          <div className="text-sm text-gray-600 text-center max-w-md">
            <p className="mb-2">Your simulation has been submitted and is being processed.</p>
            <p>Images will be analyzed and the simulation will run in the background.</p>
          </div>
        </div>
        
        {/* Email Notification Modal */}
        <Dialog open={showEmailModal} onOpenChange={setShowEmailModal}>
          <DialogContent className="sm:max-w-md">
            <CustomDialogClose onClick={() => setShowEmailModal(false)}>
              <X className="h-5 w-5" />
            </CustomDialogClose>
            <DialogHeader>
              <DialogTitle className="flex items-center">
                <Mail className="h-5 w-5 mr-2 text-blue-600" />
                Email Notification
              </DialogTitle>
              <DialogDescription>
                Enter your email address to receive a notification when your simulation is complete.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <label htmlFor="notification-email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                <input
                  id="notification-email"
                  type="email"
                  value={notificationEmail}
                  onChange={(e) => setNotificationEmail(e.target.value)}
                  placeholder="your.email@example.com"
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  className="bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-2 px-4 rounded"
                  onClick={() => setShowEmailModal(false)}
                  disabled={emailNotificationStatus === 'sending'}
                >
                  Cancel
                </button>
                <button
                  className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded disabled:opacity-60"
                  onClick={() => handleEmailNotification(notificationEmail)}
                  disabled={!notificationEmail || emailNotificationStatus === 'sending'}
                >
                  {emailNotificationStatus === 'sending' ? 'Setting up...' : 'Set Notification'}
                </button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={onBack}
            icon={<ArrowLeft className="w-4 h-4 mr-1" />}
          >
            Back to use case selection
          </Button>
          <span className="text-sm text-gray-500">{selectedSegmentIds.length} segments selected</span>
        </div>
        
        {error ? (
          <div className="text-sm text-red-600 mb-3">
            {error}
          </div>
        ) : null}
      </div>

      <div className="mb-6">
        <label htmlFor="simName" className="block text-sm font-medium text-gray-700 mb-2">
          Simulation Name
        </label>
        <input
          id="simName"
          type="text"
          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          value={simName}
          onChange={(e) => {
            setSimName(e.target.value);
            handleEdit();
          }}
          placeholder="Enter a name for your simulation"
        />
      </div>
      
      {reviewType === 'ab-test' && (
        <div className="mb-6">
          <label htmlFor="goal" className="block text-sm font-medium text-gray-700 mb-2">
            Goal
          </label>
          <input
            id="goal"
            type="text"
            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            value={goal}
            onChange={(e) => {
              setGoal(e.target.value);
              handleEdit();
            }}
            placeholder="What are you trying to achieve with this test?"
          />
        </div>
      )}

             <div className="mb-6">
         <label className="block text-sm font-medium text-gray-700 mb-2">
           Review Type
         </label>
         <RadioGroup
           value={reviewType}
           onValueChange={(val) => {
             setReviewType(val as 'creative-review' | 'ab-test');
             handleEdit();
           }}
           className="flex flex-row gap-4"
         >
           <label
             htmlFor="ab-test-radio"
             className={`
               group flex items-center cursor-pointer rounded-md border-2 px-3 py-2 transition-all flex-1 min-w-0
               ${reviewType === 'ab-test' ? 'border-blue-600 bg-blue-50 shadow' : 'border-gray-200 bg-white'}
               hover:border-blue-400 hover:shadow-sm relative
             `}
           >
             <RadioGroupItem value="ab-test" id="ab-test-radio" className="sr-only" />
             <div className="flex items-center min-w-[110px]">
               <span className={`rounded-full p-1.5 mr-2 ${reviewType === 'ab-test' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-400'}`}>
                 <TestTube className="h-5 w-5" />
               </span>
               <span className="font-semibold text-blue-900 text-sm">A/B Test</span>
             </div>
             <span className="ml-4 text-gray-600 text-xs text-left flex-1">
               Compare performance between different creative variations
             </span>
             {reviewType === 'ab-test' && (
               <span className="absolute top-2 right-2 text-blue-600">
                 <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
               </span>
             )}
           </label>
           <label
             htmlFor="creative-review-radio"
             className={`
               group flex items-center cursor-pointer rounded-md border-2 px-3 py-2 transition-all flex-1 min-w-0
               ${reviewType === 'creative-review' ? 'border-blue-600 bg-blue-50 shadow' : 'border-gray-200 bg-white'}
               hover:border-blue-400 hover:shadow-sm relative
             `}
           >
             <RadioGroupItem value="creative-review" id="creative-review-radio" className="sr-only" />
             <div className="flex items-center min-w-[140px]">
               <span className={`rounded-full p-1.5 mr-2 ${reviewType === 'creative-review' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-400'}`}>
                 <FileText className="h-5 w-5" />
               </span>
               <span className="font-semibold text-blue-900 text-sm">Creative Review</span>
             </div>
             <span className="ml-4 text-gray-600 text-xs text-left flex-1">
               Get feedback on multiple creative assets for your campaign
             </span>
             {reviewType === 'creative-review' && (
               <span className="absolute top-2 right-2 text-blue-600">
                 <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
               </span>
             )}
           </label>
         </RadioGroup>
       </div>

      {reviewType === 'creative-review' && (
        <>
                     <div className="mb-6">
             <label htmlFor="campaignObjective" className="block text-sm font-medium text-gray-700 mb-2">
               Campaign Objective
             </label>
             <textarea
               id="campaignObjective"
               rows={3}
               className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
               value={campaignObjective}
               onChange={(e) => {
                 setCampaignObjective(e.target.value);
                 handleEdit();
               }}
               placeholder="Describe the campaign objective and what you're trying to achieve..."
             />
           </div>
          
                     <div className="mb-6">
             <label htmlFor="kpis" className="block text-sm font-medium text-gray-700 mb-2">
               KPIs
             </label>
             <textarea
               id="kpis"
               rows={3}
               className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
               value={kpis}
               onChange={(e) => {
                 setKpis(e.target.value);
                 handleEdit();
               }}
               placeholder="List your key performance indicators (e.g., CTR, conversion rate, brand awareness, etc.)"
             />
           </div>
          
                     <div className="mb-6">
             <label htmlFor="companyDetails" className="block text-sm font-medium text-gray-700 mb-2">
               Company Details
             </label>
             <textarea
               id="companyDetails"
               rows={4}
               className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
               value={companyDetails}
               onChange={(e) => {
                 setCompanyDetails(e.target.value);
                 handleEdit();
               }}
               placeholder="Describe your company - industry, size, market position, values, etc."
             />
           </div>
          
                     <div className="mb-6">
             <label htmlFor="productDetails" className="block text-sm font-medium text-gray-700 mb-2">
               Product Details
             </label>
             <textarea
               id="productDetails"
               rows={4}
               className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
               value={productDetails}
               onChange={(e) => {
                 setProductDetails(e.target.value);
                 handleEdit();
               }}
               placeholder="Describe your product/service - features, benefits, target audience, pricing, etc."
             />
           </div>
          
                     <div className="mb-6">
             <label htmlFor="competitiveLandscape" className="block text-sm font-medium text-gray-700 mb-2">
               Competitive Landscape
             </label>
             <textarea
               id="competitiveLandscape"
               rows={4}
               className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
               value={competitiveLandscape}
               onChange={(e) => {
                 setCompetitiveLandscape(e.target.value);
                 handleEdit();
               }}
               placeholder="Describe your main competitors, their strategies, positioning, and how you differentiate..."
             />
           </div>
          
                     <div className="mb-6">
             <label htmlFor="mediumAndPlacement" className="block text-sm font-medium text-gray-700 mb-2">
               Medium and Placement
             </label>
             <textarea
               id="mediumAndPlacement"
               rows={3}
               className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
               value={mediumAndPlacement}
               onChange={(e) => {
                 setMediumAndPlacement(e.target.value);
                 handleEdit();
               }}
               placeholder="Specify where and how the creatives will be used (e.g., Facebook ads, Instagram stories, print ads, billboards, etc.)"
             />
           </div>
          
                     <div className="mb-6">
             <label htmlFor="culturalConsiderations" className="block text-sm font-medium text-gray-700 mb-2">
               Cultural Considerations (Optional)
             </label>
             <textarea
               id="culturalConsiderations"
               rows={3}
               className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
               value={culturalConsiderations}
               onChange={(e) => {
                 setCulturalConsiderations(e.target.value);
                 handleEdit();
               }}
               placeholder="Any cultural, regional, or sensitivity considerations for the target audience..."
             />
           </div>
          
                     <div className="mb-6">
             <label htmlFor="priorPerformanceData" className="block text-sm font-medium text-gray-700 mb-2">
               Prior Performance Data (Optional)
             </label>
             <textarea
               id="priorPerformanceData"
               rows={4}
               className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
               value={priorPerformanceData}
               onChange={(e) => {
                 setPriorPerformanceData(e.target.value);
                 handleEdit();
               }}
               placeholder="Include any relevant data from previous campaigns - performance metrics, learnings, benchmarks, etc."
             />
           </div>
        </>
      )}

      <div className="mb-6">
        <div className="flex justify-between items-center mb-2">
          <label className="block text-sm font-medium text-gray-700">
            Creative Images
          </label>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={triggerFileInput}
            aria-label="Add creative images"
            title="Add creative images"
          >
            <Upload className="w-4 h-4 mr-1" /> Add Images
          </Button>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleImageUpload}
            accept="image/png,image/jpeg,image/jpg"
            multiple
            className="hidden"
            aria-label="Upload creative images"
            title="Upload creative images"
          />
        </div>
        
        {images.length === 0 ? (
          <div 
            className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-blue-500 transition-colors"
            onClick={triggerFileInput}
          >
            <Upload className="w-8 h-8 mx-auto text-gray-400" />
            <p className="mt-2 text-sm text-gray-500">
              Click to upload or drag and drop
            </p>
            <p className="text-xs text-gray-400">
              PNG, JPG, GIF up to 10MB • Maximum 5 images
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {images.map((image) => (
              <div key={image.id} className="relative">
                <img 
                  src={image.preview} 
                  alt={`Creative ${image.id}`}
                  className="h-32 w-full object-cover rounded-md border border-gray-300"
                />
                <button
                  type="button"
                  aria-label={`Remove image`}
                  title={`Remove image`}
                  className="absolute top-1 right-1 bg-black bg-opacity-50 rounded-full p-1 text-white hover:bg-opacity-70"
                  onClick={() => removeImage(image.id)}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
            
            {images.length < 5 && (
              <div 
                className="h-32 border-2 border-dashed border-gray-300 rounded-md flex flex-col items-center justify-center cursor-pointer hover:border-blue-500 transition-colors"
                onClick={triggerFileInput}
              >
                <Upload className="w-6 h-6 text-gray-400" />
                <span className="mt-1 text-xs text-gray-500">Add more</span>
              </div>
            )}
          </div>
        )}
      </div>
      
      <div className="flex justify-end">
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={!isFormValid || isSubmitting}
          withArrow
        >
          {isSubmitting 
            ? (reviewType === 'creative-review' ? 'Generating Review...' : 'Running Test...') 
            : saveMode ? 'Continue' : (reviewType === 'creative-review' ? 'Generate Creative Review' : 'Run A/B Test')
          }
        </Button>
      </div>
    </div>
  );
};

export default ABTestCreativesForm;